import { computed } from "mobx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePropertyAiGradeStore } from "..";
import type IProperty from "../interfaces/IProperty";
import type { IBase64ScreenshotSet } from "../services/propertyAiGradeApiService";

export default function usePropertyScreenshots(property: IProperty | undefined): IPropertyScreenshotsInfo {
    const aiGradeStore = usePropertyAiGradeStore();
    const [screenshots, setScreenshots] = useState<IBase64ScreenshotSet | undefined>();
    const isLoading = useMemo(() => computed(() => aiGradeStore.getIsLoadingFor(property?.id)), [property?.id]).get();
    const isError = useMemo(() => computed(() => aiGradeStore.getIsErrorFor(property?.id)), [property?.id]).get();

    useEffect(() => setScreenshots(undefined), [property?.id]);

    const fetchScreenshots = useCallback(async () => {
        if (!property || aiGradeStore.getIsLoadingFor(property.id)) return;

        aiGradeStore.startLoading(property.id);

        try {
            const result = await aiGradeStore.getScreenshotsForAsync(property.id);
            setScreenshots(result);
        } catch {
            aiGradeStore.setIsErrorFor(property.id, true);
        } finally {
            aiGradeStore.stopLoading(property.id);
        }
    }, [property, aiGradeStore]);

    return useMemo(() => ({
        screenshots,
        isLoading,
        isError,
        fetchScreenshots,
    }), [screenshots, isLoading, isError, fetchScreenshots]);
}

export interface IPropertyScreenshotsInfo {
    screenshots?: IBase64ScreenshotSet;
    isLoading: boolean;
    isError: boolean;
    fetchScreenshots: () => void;
}
